import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FiBarChart2, FiTrendingUp, FiUsers, FiCalendar } from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { responsesAPI } from '../services/api';

const AnalyticsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
`;

const PageDescription = styled.p`
  color: #6b7280;
  font-size: 16px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => props.color || '#3b82f6'};
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${props => props.color || '#3b82f6'}20;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color || '#3b82f6'};
  font-size: 20px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
`;

const StatChange = styled.div`
  font-size: 12px;
  color: ${props => props.positive ? '#10b981' : '#ef4444'};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 24px;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
`;

const ChartContainer = styled.div`
  height: 300px;
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 32px;
`;

const FilterTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #6b7280;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyStateTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
`;

function Analytics() {
  const { formId } = useParams();
  const [analytics, setAnalytics] = useState({
    total_responses: 0,
    daily_responses: [],
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    form_id: formId || '',
  });

  useEffect(() => {
    if (formId) {
      setFilters(prev => ({ ...prev, form_id: formId }));
    } else {
      setFilters(prev => ({ ...prev, form_id: '' }));
    }
  }, [formId]);

  useEffect(() => {
    loadAnalytics();
  }, [filters]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      console.log('Loading analytics with filters:', filters);
      const response = await responsesAPI.getAnalytics(filters);
      console.log('Analytics response:', response.data);
      setAnalytics(response.data.data);
    } catch (error) {
      toast.error('Failed to load analytics');
      console.error('Analytics error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      form_id: formId || '', // Keep form_id if we're on a form-specific page
    });
  };

  // Calculate additional stats
  const totalResponses = analytics.total_responses || 0;
  const dailyResponses = analytics.daily_responses || [];
  
  // Calculate this week's responses
  const thisWeekResponses = dailyResponses
    .filter(item => {
      const date = new Date(item.date);
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return date >= weekAgo;
    })
    .reduce((sum, item) => sum + item.count, 0);

  // Calculate this month's responses
  const thisMonthResponses = dailyResponses
    .filter(item => {
      const date = new Date(item.date);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    })
    .reduce((sum, item) => sum + item.count, 0);

  // Calculate average daily responses
  const averageDaily = dailyResponses.length > 0 
    ? Math.round(dailyResponses.reduce((sum, item) => sum + item.count, 0) / dailyResponses.length)
    : 0;

  // Format data for charts
  const chartData = dailyResponses
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      responses: item.count,
      fullDate: item.date,
    }));

  // Calculate top days
  const topDays = [...dailyResponses]
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
    .map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      responses: item.count,
    }));

  if (loading) {
    return (
      <AnalyticsContainer>
        <LoadingSpinner>Loading analytics...</LoadingSpinner>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <PageHeader>
        <PageTitle>
          {formId ? 'Form Analytics' : 'Analytics Dashboard'}
        </PageTitle>
        <PageDescription>
          {formId 
            ? 'Track performance and response trends for this specific form'
            : 'Track your form performance and response trends across all forms'
          }
        </PageDescription>
        {formId && (
          <div style={{ 
            marginTop: '12px', 
            padding: '8px 12px', 
            backgroundColor: '#f0f9ff', 
            border: '1px solid #0ea5e9', 
            borderRadius: '6px',
            fontSize: '14px',
            color: '#0c4a6e'
          }}>
            <strong>Form ID:</strong> {formId}
          </div>
        )}
      </PageHeader>

      <FilterSection>
        <FilterTitle>Filters</FilterTitle>
        <FilterGrid>
          <FilterGroup>
            <FilterLabel>From Date</FilterLabel>
            <FilterInput
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
            />
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>To Date</FilterLabel>
            <FilterInput
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
            />
          </FilterGroup>
          {!formId && (
            <FilterGroup>
              <FilterLabel>Form ID</FilterLabel>
              <FilterInput
                type="text"
                placeholder="Filter by form ID"
                value={filters.form_id}
                onChange={(e) => handleFilterChange('form_id', e.target.value)}
              />
            </FilterGroup>
          )}
          <FilterGroup>
            <FilterLabel>&nbsp;</FilterLabel>
            <button
              onClick={clearFilters}
              style={{
                padding: '8px 16px',
                background: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Clear Filters
            </button>
          </FilterGroup>
        </FilterGrid>
      </FilterSection>

      <StatsGrid>
        <StatCard color="#3b82f6">
          <StatHeader>
            <StatIcon color="#3b82f6">
              <FiBarChart2 />
            </StatIcon>
          </StatHeader>
          <StatValue>{totalResponses}</StatValue>
          <StatLabel>Total Responses</StatLabel>
          <StatChange positive={thisMonthResponses > 0}>
            <FiTrendingUp />
            {thisMonthResponses} this month
          </StatChange>
        </StatCard>

        <StatCard color="#10b981">
          <StatHeader>
            <StatIcon color="#10b981">
              <FiUsers />
            </StatIcon>
          </StatHeader>
          <StatValue>{thisWeekResponses}</StatValue>
          <StatLabel>This Week</StatLabel>
          <StatChange positive={thisWeekResponses > 0}>
            <FiTrendingUp />
            {Math.round((thisWeekResponses / 7) * 7)} responses
          </StatChange>
        </StatCard>

        <StatCard color="#f59e0b">
          <StatHeader>
            <StatIcon color="#f59e0b">
              <FiCalendar />
            </StatIcon>
          </StatHeader>
          <StatValue>{averageDaily}</StatValue>
          <StatLabel>Daily Average</StatLabel>
          <StatChange positive={averageDaily > 0}>
            <FiTrendingUp />
            responses per day
          </StatChange>
        </StatCard>

        <StatCard color="#8b5cf6">
          <StatHeader>
            <StatIcon color="#8b5cf6">
              <FiBarChart2 />
            </StatIcon>
          </StatHeader>
          <StatValue>{thisMonthResponses}</StatValue>
          <StatLabel>This Month</StatLabel>
          <StatChange positive={thisMonthResponses > 0}>
            <FiTrendingUp />
            {Math.round(thisMonthResponses / 30)} per day
          </StatChange>
        </StatCard>
      </StatsGrid>

      {chartData.length === 0 ? (
        <EmptyState>
          <EmptyStateTitle>No data available</EmptyStateTitle>
          <p>No responses found for the selected date range.</p>
        </EmptyState>
      ) : (
        <ChartsGrid>
          <ChartCard>
            <ChartHeader>
              <ChartTitle>Response Trends</ChartTitle>
            </ChartHeader>
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="responses" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </ChartCard>

          <ChartCard>
            <ChartHeader>
              <ChartTitle>Top Days</ChartTitle>
            </ChartHeader>
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topDays} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="date" type="category" width={60} />
                  <Tooltip />
                  <Bar dataKey="responses" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </ChartCard>
        </ChartsGrid>
      )}
    </AnalyticsContainer>
  );
}

export default Analytics;
